<script lang="ts">
  import { reactive } from 'vue';
  const syncState = reactive<Record<string, boolean>>({});
</script>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg?component';

  interface MarginValues {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }

  interface Props {
    modelValue: MarginValues;
    elementId?: string;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    'update:modelValue': [value: MarginValues];
  }>();

  const localSynced = ref(false);

  const synced = computed({
    get: () => (props.elementId ? (syncState[props.elementId] ?? false) : localSynced.value),
    set: (val: boolean) => {
      if (props.elementId) {
        syncState[props.elementId] = val;
      } else {
        localSynced.value = val;
      }
    },
  });

  function toggleSync() {
    synced.value = !synced.value;
    if (synced.value) {
      const val = props.modelValue.top;
      emit('update:modelValue', { top: val, right: val, bottom: val, left: val });
    }
  }

  function onInput(side: keyof MarginValues, val: number) {
    if (synced.value) {
      emit('update:modelValue', { top: val, right: val, bottom: val, left: val });
    } else {
      emit('update:modelValue', { ...props.modelValue, [side]: val });
    }
  }
</script>

<template>
  <div class="flex flex-col gap-2 mt-2">
    <p class="text-sm text-gray-11">
      Externo
    </p>
    <div class="flex items-center gap-2">
      <div class="flex-1 flex flex-col gap-2 relative pl-3">
        <svg
          v-if="synced"
          class="h-full overflow-visible absolute left-0 top-0 w-3 pointer-events-none"
          aria-hidden="true"
        >
          <line
            x1="4"
            y1="41"
            x2="4"
            y2="111"
            stroke="var(--color-gray-02)"
            stroke-width="2"
            stroke-linecap="round"
          />
          <line
            x1="4"
            y1="41"
            x2="12"
            y2="41"
            stroke="var(--color-gray-02)"
            stroke-width="2"
            stroke-linecap="round"
          />
          <line
            x1="4"
            y1="111"
            x2="12"
            y2="111"
            stroke="var(--color-gray-02)"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <!-- Row 1: Superior + Inferior -->
        <div class="flex gap-2 relative">
          <div
            v-if="synced"
            class="left-[calc(50%-4px)] w-2 top-10 absolute h-0.5 bg-gray-02 pointer-events-none"
          />
          <div class="flex flex-col gap-1 flex-1">
            <p class="text-sm text-gray-11">
              Superior
            </p>
            <div
              class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            >
              <input
                type="number"
                min="0"
                max="100"
                :value="modelValue.top"
                class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
                @input="(e) => onInput('top', Number((e.target as HTMLInputElement).value))"
              >
              <span class="text-gray-03 text-sm shrink-0">px</span>
            </div>
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <p class="text-sm text-gray-11">
              Inferior
            </p>
            <div
              class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            >
              <input
                type="number"
                min="0"
                max="100"
                :value="modelValue.bottom"
                class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
                @input="(e) => onInput('bottom', Number((e.target as HTMLInputElement).value))"
              >
              <span class="text-gray-03 text-sm shrink-0">px</span>
            </div>
          </div>
        </div>
        <!-- Row 2: Direita + Esquerda -->
        <div class="flex gap-2 relative">
          <div
            v-if="synced"
            class="left-[calc(50%-4px)] w-2 top-10 absolute h-0.5 bg-gray-02 pointer-events-none"
          />
          <div class="flex flex-col gap-1 flex-1">
            <p class="text-sm text-gray-11">
              Direita
            </p>
            <div
              class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            >
              <input
                type="number"
                min="0"
                max="100"
                :value="modelValue.right"
                class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
                @input="(e) => onInput('right', Number((e.target as HTMLInputElement).value))"
              >
              <span class="text-gray-03 text-sm shrink-0">px</span>
            </div>
          </div>
          <div class="flex flex-col gap-1 flex-1">
            <p class="text-sm text-gray-11">
              Esquerda
            </p>
            <div
              class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            >
              <input
                type="number"
                min="0"
                max="100"
                :value="modelValue.left"
                class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
                @input="(e) => onInput('left', Number((e.target as HTMLInputElement).value))"
              >
              <span class="text-gray-03 text-sm shrink-0">px</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Link/sync button -->
      <button
        type="button"
        :class="[
          'border flex items-center justify-center rounded-lg shrink-0 size-8 transition-colors',
          synced ? 'border-[var(--theme-secondary-ink)] bg-[var(--theme-secondary-tint)]' : 'bg-white-01 border-gray-02 hover:border-gray-03',
        ]"
        @click="toggleSync"
      >
        <LinkIcon
          class="block shrink-0 scale-[0.85]"
          :style="{ color: synced ? 'var(--theme-secondary-ink)' : 'var(--color-gray-800)' }"
        />
      </button>
    </div>
  </div>
</template>
